import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { tokens } from '@companycam/slab';

import Tab from './Tab';

// #region styled-components
const Container = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`;

const TabList = styled.ul`
  margin: 0;
  padding: 0;
  list-style-type: none;
  display: flex;
  align-items: flex-end;
  border-block-end: ${tokens.size_border_width_s}px solid
    ${tokens.color_border_default_le};
`;

const TabsListItem = styled.li`
  margin: 0 ${tokens.size_spacing_m}px 0 0;
  padding: 0;
`;

const TabContentContainer = styled.div`
  overflow-y: auto;
`;

const tabContentAnimation = keyframes`
  to { opacity: 1; }
`;

const TabContent = styled.div`
  ${(props) => (props.active ? '' : 'display: none;')}
  opacity: 0;
  animation-name: ${tabContentAnimation};
  animation-duration: 0.3s;
  animation-fill-mode: forwards;
  animation-timing-function: ease-out;
`;
// #endregion

const Tabs = ({
  children,
  id,
  className,
  style,
  tabListStyle,
  tabContentContainerStyle,
  tabContentStyle,
  onTabSelected
}) => {
  const [activeTab, setActiveTab] = useState(children[0].props.label);

  const onTabClick = (tab) => {
    setActiveTab(tab);

    if (onTabSelected) {
      onTabSelected(tab);
    }
  };

  return (
    <Container className={className} style={style}>
      <TabList role="tablist" style={tabListStyle}>
        {children.map((child, index) => {
          const { label } = child.props;
          return (
            <TabsListItem key={label} role="presentation">
              <Tab
                activeTab={activeTab}
                label={label}
                onClick={onTabClick}
                id={`${id}-tab-${index}`}
              />
            </TabsListItem>
          );
        })}
      </TabList>
      <TabContentContainer style={tabContentContainerStyle}>
        {children.map((child, index) => {
          const { label, children: activeTabContent } = child.props;
          const isSelected = activeTab === label;
          // To avoid dismounting components and interrupting state updates or—
          // network requests while switching tabs
          // we use CSS to handle displaying tab contents instead of
          // `isActiveTab ? activeContent : null`
          return (
            <TabContent
              key={label}
              active={isSelected}
              role="tabpanel"
              aria-hidden={!isSelected}
              aria-labelledby={`${id}-tab-${index}`}
              style={tabContentStyle}
            >
              {activeTabContent}
            </TabContent>
          );
        })}
      </TabContentContainer>
    </Container>
  );
};

Tabs.propTypes = {
  children: PropTypes.instanceOf(Array).isRequired,
  id: PropTypes.string.isRequired,
  onTabSelected: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  tabListStyle: PropTypes.object,
  tabContentContainerStyle: PropTypes.object,
  tabContentStyle: PropTypes.object,
};

Tabs.defaultProps = {
  onTabSelected: undefined,
  className: undefined,
  style: undefined,
  tabListStyle: undefined,
  tabContentContainerStyle: undefined,
  tabContentStyle: undefined,
};

export default Tabs;
